/**
 * Token Refresh Service
 * Handles automatic token refresh to prevent unnecessary logouts
 */

const MAX_RETRY_ATTEMPTS = 2;
let refreshPromise = null;

/**
 * Attempts to refresh the authentication token using the tick endpoint
 * @returns {Promise<boolean>} - True if refresh successful, false otherwise
 */
export const refreshAuthToken = async () => {
  // Prevent multiple simultaneous refresh attempts
  if (refreshPromise) {
    console.log('🔄 Token refresh already in progress, waiting...');
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    try {
      if (isDevMode) {
        console.log('🔄 Attempting to refresh auth token via tick endpoint...');
      }

      const response = await fetch(
        `${process.env.REACT_APP_PROXY_SERVER_URL || 'https://api.aristai.io'}/api/auth/tick`,
        {
          method: 'GET',
          credentials: 'include',
          cache: 'no-cache',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.isLogged === true) {
          if (isDevMode) {
            console.log('✅ Token refresh successful');
          }
          return true;
        }
      }
      
      if (isDevMode) {
        console.warn('⚠️ Token refresh failed - response not ok or not logged in');
      }
      return false;
    } catch (error) {
      if (isDevMode) {
        console.error('❌ Token refresh error:', error);
      }
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Attempts to refresh token with retry logic
 * @param {number} maxAttempts - Maximum number of retry attempts
 * @returns {Promise<boolean>} - True if any attempt succeeds
 */
export const refreshWithRetry = async (maxAttempts = MAX_RETRY_ATTEMPTS) => {
  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      // Exponential backoff: 1s, 2s, 4s...
      const delay = Math.pow(2, attempt - 1) * 1000;
      if (isDevMode) {
        console.log(`⏳ Waiting ${delay}ms before retry attempt ${attempt + 1}/${maxAttempts}...`);
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    const success = await refreshAuthToken();
    if (success) {
      return true;
    }
  }
  
  if (isDevMode) {
    console.error(`❌ All ${maxAttempts} token refresh attempts failed`);
  }
  return false;
};

/**
 * Checks if we should attempt token refresh
 * Can be extended to check various conditions
 * @returns {boolean} - True if refresh should be attempted
 */
export const shouldAttemptRefresh = () => {
  // Check if feature is enabled
  const autoRefreshEnabled = process.env.REACT_APP_ENABLE_AUTO_REFRESH !== 'false';
  
  if (!autoRefreshEnabled) {
    console.log('⚠️ Auto-refresh is disabled via REACT_APP_ENABLE_AUTO_REFRESH');
    return false;
  }
  
  // Could add more conditions here, such as:
  // - Check if we're on a public page that doesn't need auth
  // - Check if we're already on the login page
  // - Check rate limiting
  
  return true;
};

const tokenRefreshService = {
  refreshAuthToken,
  refreshWithRetry,
  shouldAttemptRefresh
};

export default tokenRefreshService;